import { ContactProps } from '@/models/Contact';
import { DepartmentTypeProps } from '@/models/DepartmentType';

export type ContactSelectOption = {
  id: string;
  label: string;
  detailLine: string;
  fallbackDetailLine: string;
  lastUpdatedText: string;
  selectedLabel: string;
};

function hasValue(value?: string | null): boolean {
  return (value ?? '').trim().length > 0;
}

function toTimestamp(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function getContactLastUpdatedTimestamp(contact: ContactProps): number | null {
  const contactRecord = contact as unknown as Record<string, unknown>;
  const contactDateKeys = [
    'updatedOnUTCDateTime',
    'modifiedOnUTCDateTime',
    'lastUpdatedOnUTCDateTime',
    'createdOnUTCDateTime',
  ];

  const timestamps: number[] = [];
  for (const key of contactDateKeys) {
    const ts = toTimestamp(contactRecord[key]);
    if (ts != null) {
      timestamps.push(ts);
    }
  }

  for (const relationship of contact.contactRelationships ?? []) {
    const ts = toTimestamp(relationship.createdOnUTCDateTime);
    if (ts != null) {
      timestamps.push(ts);
    }
  }

  if (timestamps.length === 0) {
    return null;
  }

  return Math.max(...timestamps);
}

function getContactCompletenessScore(contact: ContactProps): number {
  let score = 0;
  if (hasValue(contact.firstName)) score += 1;
  if (hasValue(contact.lastName)) score += 1;
  if (hasValue(contact.email)) score += 1;
  if (hasValue(contact.phone)) score += 1;
  if (hasValue(contact.title)) score += 1;
  if (hasValue(contact.departmentOther)) score += 1;
  if (contact.departmentTypeId != null) score += 1;
  if (hasValue(contact.streetAddress1)) score += 1;
  if (hasValue(contact.city)) score += 1;
  if (hasValue(contact.zipCode)) score += 1;
  if (contact.stateId != null) score += 1;
  if (contact.countryId != null) score += 1;
  if (hasValue(contact.linkedIn)) score += 1;
  if (hasValue(contact.whatsApp)) score += 1;
  if (hasValue(contact.certification)) score += 1;
  return score;
}

function getContactSortScore(contact: ContactProps): number {
  const recencyTs = getContactLastUpdatedTimestamp(contact) ?? 0;
  const completeness = getContactCompletenessScore(contact);
  return recencyTs * 100 + completeness;
}

export function getPreferredContact(a: ContactProps, b: ContactProps): ContactProps {
  const aScore = getContactSortScore(a);
  const bScore = getContactSortScore(b);
  if (aScore !== bScore) {
    return aScore > bScore ? a : b;
  }

  const aId = a.id ?? Number.MIN_SAFE_INTEGER;
  const bId = b.id ?? Number.MIN_SAFE_INTEGER;
  return aId >= bId ? a : b;
}

export function buildContactSelectOptions(
  contacts: ContactProps[] | null | undefined,
  departmentTypes: DepartmentTypeProps[] | undefined
): ContactSelectOption[] {
  const contactsWithId = (contacts ?? []).filter((contact) => contact.id != null);
  const departmentTypeNameById = new Map<number, string>(
    (departmentTypes ?? []).map((departmentType) => [departmentType.id, departmentType.name])
  );

  const sortedContacts = [...contactsWithId].sort((a, b) => {
    const recencyDelta =
      (getContactLastUpdatedTimestamp(b) ?? Number.MIN_SAFE_INTEGER) -
      (getContactLastUpdatedTimestamp(a) ?? Number.MIN_SAFE_INTEGER);
    if (recencyDelta !== 0) {
      return recencyDelta;
    }

    const completenessDelta = getContactCompletenessScore(b) - getContactCompletenessScore(a);
    if (completenessDelta !== 0) {
      return completenessDelta;
    }

    return (b.id ?? 0) - (a.id ?? 0);
  });

  const nameCounts = new Map<string, number>();
  for (const contact of sortedContacts) {
    const key = `${(contact.firstName ?? '').trim().toLowerCase()}|${(contact.lastName ?? '')
      .trim()
      .toLowerCase()}`;
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
  }

  const nameGroups = new Map<string, ContactProps[]>();
  for (const contact of sortedContacts) {
    const key = `${(contact.firstName ?? '').trim().toLowerCase()}|${(contact.lastName ?? '')
      .trim()
      .toLowerCase()}`;
    const existingGroup = nameGroups.get(key) ?? [];
    existingGroup.push(contact);
    nameGroups.set(key, existingGroup);
  }

  function getLastUpdatedText(contact: ContactProps): string {
    const ts = getContactLastUpdatedTimestamp(contact);
    if (ts == null) {
      return '';
    }

    return new Date(ts).toLocaleDateString();
  }

  function getDepartmentText(contact: ContactProps): string {
    if (hasValue(contact.departmentOther)) {
      return (contact.departmentOther ?? '').trim();
    }

    if (contact.departmentTypeId != null) {
      return (
        departmentTypeNameById.get(contact.departmentTypeId) ??
        `Department ${contact.departmentTypeId}`
      );
    }

    return '';
  }

  return sortedContacts.map((contact) => {
    const firstName = (contact.firstName ?? '').trim();
    const lastName = (contact.lastName ?? '').trim();
    const email = (contact.email ?? '').trim();

    const fullName = `${firstName} ${lastName}`.trim();
    const displayName = fullName.length > 0 ? fullName : 'Unnamed contact';
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
    const hasDuplicateName = (nameCounts.get(key) ?? 0) > 1;
    const sameNameGroup = nameGroups.get(key) ?? [contact];
    const departmentText = getDepartmentText(contact);
    const phoneText = (contact.phone ?? '').trim();
    const titleText = (contact.title ?? '').trim();
    const lastUpdatedText = getLastUpdatedText(contact);

    const detailParts = [
      phoneText.length > 0 ? `Phone: ${phoneText}` : '',
      departmentText.length > 0 ? `Department: ${departmentText}` : '',
      titleText.length > 0 ? `Title: ${titleText}` : '',
    ].filter((value) => value.length > 0);

    const detailsLine = detailParts.slice(0, 2).join(' | ');
    const fallbackDetailsLine =
      detailParts.length > 2
        ? `${detailParts[2]}`
        : detailParts.length === 0
          ? 'No additional details'
          : '';

    const primaryLabel = email.length > 0 ? `${displayName} (${email})` : displayName;

    if (!hasDuplicateName) {
      return {
        id: contact.id!.toString(),
        label: primaryLabel,
        detailLine: detailsLine,
        fallbackDetailLine: fallbackDetailsLine,
        lastUpdatedText,
        selectedLabel: primaryLabel,
      };
    }

    const disambiguator =
      email.length > 0
        ? email
        : (contact.phone ?? '').trim().length > 0
          ? (contact.phone ?? '').trim()
          : `ID: ${contact.id}`;

    const duplicateCount = sameNameGroup.length;

    return {
      id: contact.id!.toString(),
      label: `${displayName} (${disambiguator})`,
      detailLine:
        detailsLine.length > 0 ? detailsLine : `Possible duplicate group (${duplicateCount})`,
      fallbackDetailLine: fallbackDetailsLine,
      lastUpdatedText,
      selectedLabel: `${displayName} (${disambiguator})`,
    };
  });
}
